// US-HISTORY-01 -- child sees their own history; guardian sees a child's history.
// US-WITHDRAWAL-05 -- guardians can mark/unmark posted withdrawals as reconciled.

import {
  type AppError,
  type HistoryEntry,
  historyList,
  withdrawalSetReconciled,
} from '@chorepop/core/client';
import { type Component, For, Match, Show, Switch, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { ReportContentButton } from '../components/ReportContentButton';
import type { ReportConcernContext } from '../lib/reportConcern';
import { isFamilyContentHidden } from '../state/familyContentVisibility';

const historyContentLocalId = (entry: HistoryEntry): string => {
  if (entry.kind === 'deposit') return `deposit:${entry.depositId}`;
  if (entry.kind === 'withdrawal') return `withdrawal:${entry.withdrawalId}`;
  return `transfer:${entry.transferId}`;
};

type Props = {
  sessionId: string;
  /** For guardians, the child being viewed. For children, pass their own id (backend ignores it). */
  childAccountId: number;
  childDisplayName: string;
  currencyCode: string;
  /** US-WITHDRAWAL-05: when true, posted withdrawals show a Mark/Unmark
   *  reconciled toggle. Children never see the toggle (this prop is
   *  false for them). The backend also rejects the call from a child
   *  session, so this is purely a UI affordance. */
  isGuardian: boolean;
  onBack: () => void;
};

export const HistoryScreen: Component<Props> = (props) => {
  // US-HISTORY-04 -- filter signals. Tracking the four filter axes
  // (date range, kind, bucket) and refetching when any change.
  const [atFromUtc, setAtFromUtc] = createSignal<string>('');
  const [atToUtc, setAtToUtc] = createSignal<string>('');
  const [kindFilter, setKindFilter] = createSignal<'' | 'deposit' | 'withdrawal' | 'transfer'>('');
  const [bucketIdFilter, setBucketIdFilter] = createSignal<number | ''>('');

  // The resource source is a tuple of every filter signal so any
  // change re-fetches. Returning the trimmed/normalized form makes
  // the comparison stable.
  const filterSource = () => [atFromUtc(), atToUtc(), kindFilter(), bucketIdFilter()] as const;

  const [entries, { refetch }] = createResource(filterSource, async (filter) => {
    const [from, to, kind, bucket] = filter;
    const result = await historyList({
      sessionId: props.sessionId,
      childAccountId: props.childAccountId,
      ...(from !== '' ? { atFromUtc: dateInputToIso(from, 'start') } : {}),
      ...(to !== '' ? { atToUtc: dateInputToIso(to, 'end') } : {}),
      ...(kind !== '' ? { kindFilter: kind } : {}),
      ...(bucket !== '' ? { bucketIdFilter: bucket } : {}),
    });
    if (!result.ok) throw new Error(result.error.message);
    return result.value;
  });

  /** Convert HTML date input value (YYYY-MM-DD) into an ISO 8601 UTC
   *  string. 'start' = midnight UTC, 'end' = end of day UTC. */
  function dateInputToIso(d: string, edge: 'start' | 'end'): string {
    return edge === 'start' ? `${d}T00:00:00.000Z` : `${d}T23:59:59.999Z`;
  }

  /** Collect bucket ids that appear anywhere in the current entries
   *  so the bucket filter dropdown only offers buckets the child
   *  has actually touched. */
  const knownBuckets = () => {
    const e = entries();
    if (!e) return [] as { bucketId: number; bucketName: string }[];
    const map = new Map<number, string>();
    for (const entry of e) {
      if (entry.kind === 'withdrawal') {
        map.set(entry.bucketId, entry.bucketName);
      } else if (entry.kind === 'deposit') {
        for (const a of entry.allocations) {
          map.set(a.bucketId, a.bucketName);
        }
      } else {
        map.set(entry.sourceBucketId, entry.sourceBucketName);
        if (entry.targetBucketId !== null && entry.targetBucketName !== null) {
          map.set(entry.targetBucketId, entry.targetBucketName);
        }
      }
    }
    return Array.from(map.entries()).map(([bucketId, bucketName]) => ({ bucketId, bucketName }));
  };

  const clearFilters = () => {
    setAtFromUtc('');
    setAtToUtc('');
    setKindFilter('');
    setBucketIdFilter('');
  };
  const anyFilterActive = () =>
    atFromUtc() !== '' || atToUtc() !== '' || kindFilter() !== '' || bucketIdFilter() !== '';

  // US-WITHDRAWAL-05 -- track which row is currently busy so we can
  // disable the button. We refetch after a successful toggle so the
  // updated `reconciled` flag flows back through the resource.
  const [busyWithdrawalId, setBusyWithdrawalId] = createSignal<number | null>(null);
  const [errorMsg, setErrorMsg] = createSignal<string | null>(null);

  const toggleReconciled = async (withdrawalId: number, currentlyReconciled: boolean) => {
    setBusyWithdrawalId(withdrawalId);
    setErrorMsg(null);
    try {
      const r = await withdrawalSetReconciled({
        sessionId: props.sessionId,
        withdrawalId,
        reconciled: !currentlyReconciled,
      });
      if (!r.ok) {
        setErrorMsg(humanReadable(r.error));
        return;
      }
      await refetch();
    } finally {
      setBusyWithdrawalId(null);
    }
  };

  return (
    <ActionScreen
      class="action-screen--history"
      eyebrow="Activity"
      title={`${props.childDisplayName}'s history`}
      onBack={props.onBack}
    >
      {/* US-HISTORY-04 -- filter row. Date range + kind + bucket.
          The bucket dropdown only lists buckets that appear in the
          current result set so the filter doesn't offer empty
          options. Filters re-apply immediately via the resource's
          source signal. */}
      <section
        class="history-filter-card"
        style="display: flex; flex-wrap: wrap; gap: 8px; align-items: end;"
        data-testid="history-filter-bar"
      >
        <label style="display: flex; flex-direction: column; gap: 4px;">
          <span class="field-label" style="font-size: 12px;">
            From
          </span>
          <input
            type="date"
            value={atFromUtc()}
            onInput={(e) => setAtFromUtc(e.currentTarget.value)}
            data-testid="history-filter-from"
          />
        </label>
        <label style="display: flex; flex-direction: column; gap: 4px;">
          <span class="field-label" style="font-size: 12px;">
            To
          </span>
          <input
            type="date"
            value={atToUtc()}
            onInput={(e) => setAtToUtc(e.currentTarget.value)}
            data-testid="history-filter-to"
          />
        </label>
        <label style="display: flex; flex-direction: column; gap: 4px;">
          <span class="field-label" style="font-size: 12px;">
            Type
          </span>
          <select
            value={kindFilter()}
            onChange={(e) =>
              setKindFilter(e.currentTarget.value as '' | 'deposit' | 'withdrawal' | 'transfer')
            }
            data-testid="history-filter-kind"
          >
            <option value="">All</option>
            <option value="deposit">Deposits only</option>
            <option value="withdrawal">Withdrawals only</option>
            <option value="transfer">Transfers only</option>
          </select>
        </label>
        <Show when={knownBuckets().length > 0}>
          <label style="display: flex; flex-direction: column; gap: 4px;">
            <span class="field-label" style="font-size: 12px;">
              Bucket
            </span>
            <select
              value={bucketIdFilter()}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setBucketIdFilter(v === '' ? '' : Number(v));
              }}
              data-testid="history-filter-bucket"
            >
              <option value="">Any bucket</option>
              <For each={knownBuckets()}>
                {(b) => <option value={b.bucketId}>{b.bucketName}</option>}
              </For>
            </select>
          </label>
        </Show>
        <Show when={anyFilterActive()}>
          <button
            type="button"
            class="ghost"
            onClick={clearFilters}
            data-testid="history-filter-clear"
          >
            Clear filters
          </button>
        </Show>
      </section>

      <Show when={errorMsg()}>
        {(msg) => (
          <div class="form-error" role="alert">
            {msg()}
          </div>
        )}
      </Show>

      <Switch>
        <Match when={entries.loading}>
          <section class="history-empty-card">
            <p>Loading history…</p>
          </section>
        </Match>

        <Match when={entries.error}>
          <section class="history-empty-card">
            <p class="form-error" role="alert">
              Couldn't load history: {(entries.error as Error).message}
            </p>
          </section>
        </Match>

        <Match when={entries() && entries()?.length === 0}>
          <section class="history-empty-card">
            <p>No history yet. Deposits and spends will appear here.</p>
          </section>
        </Match>

        <Match when={(entries()?.length ?? 0) > 0}>
          <ul class="history-list">
            <For
              each={(entries() ?? []).filter(
                (entry) => !isFamilyContentHidden(historyContentLocalId(entry)),
              )}
            >
              {(entry) => (
                <HistoryRow
                  entry={entry}
                  currencyCode={props.currencyCode}
                  childDisplayName={props.childDisplayName}
                  isGuardian={props.isGuardian}
                  busyWithdrawalId={busyWithdrawalId()}
                  onToggleReconciled={toggleReconciled}
                />
              )}
            </For>
          </ul>
        </Match>
      </Switch>
    </ActionScreen>
  );
};

const HistoryRow: Component<{
  entry: HistoryEntry;
  currencyCode: string;
  childDisplayName: string;
  isGuardian: boolean;
  busyWithdrawalId: number | null;
  onToggleReconciled: (withdrawalId: number, currentlyReconciled: boolean) => Promise<void>;
}> = (props) => {
  const reportContext = (): ReportConcernContext | null => {
    const entry = props.entry;
    if (entry.kind === 'deposit') {
      if (!entry.note && !entry.decisionReason) return null;
      return {
        contentKind: 'deposit',
        contentLocalId: `deposit:${entry.depositId}`,
        label: 'deposit note or reply',
        contentSnapshot: {
          depositId: entry.depositId,
          childDisplayName: props.childDisplayName,
          sourceLabel: entry.sourceLabel,
          note: entry.note,
          decisionReason: entry.decisionReason,
          decidedByDisplayName: entry.decidedByDisplayName,
          statusCode: entry.statusCode,
        },
      };
    }
    if (entry.kind === 'withdrawal') {
      if (!entry.reasonNote && !entry.decisionReason) return null;
      return {
        contentKind: 'withdrawal',
        contentLocalId: `withdrawal:${entry.withdrawalId}`,
        label: 'spending note or reply',
        contentSnapshot: {
          withdrawalId: entry.withdrawalId,
          childDisplayName: props.childDisplayName,
          reasonLabel: entry.reasonLabel,
          reasonNote: entry.reasonNote,
          decisionReason: entry.decisionReason,
          decidedByDisplayName: entry.decidedByDisplayName,
          statusCode: entry.statusCode,
        },
      };
    }
    if (!entry.note && !entry.decisionReason) return null;
    return {
      contentKind: 'approval',
      contentLocalId: `transfer:${entry.transferId}`,
      label: 'money-move note or reply',
      contentSnapshot: {
        transferId: entry.transferId,
        childDisplayName: props.childDisplayName,
        note: entry.note,
        decisionReason: entry.decisionReason,
        decidedByDisplayName: entry.decidedByDisplayName,
        statusCode: entry.statusCode,
      },
    };
  };

  const formatAmount = (minor: number) => {
    const major = minor / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: props.currencyCode,
      }).format(major);
    } catch {
      return `${props.currencyCode} ${major.toFixed(2)}`;
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  return (
    <Switch>
      <Match when={props.entry.kind === 'deposit' && props.entry}>
        {(e) => (
          <li class="history-row history-row--deposit">
            <div class="history-row-leading">
              <span class="history-sign history-sign--plus">+</span>
              <span class="history-amount">{formatAmount(e().amountMinorUnits)}</span>
            </div>
            <div class="history-row-body">
              <div class="history-row-title">Deposit · {e().sourceLabel}</div>
              <Show when={e().note}>
                {(note) => <div class="history-row-note">"{note()}"</div>}
              </Show>
              <div class="history-row-allocations">
                <For each={e().allocations}>
                  {(a) => (
                    <span class="history-chip">
                      {a.bucketName} +{formatAmount(a.amountMinorUnits)}
                    </span>
                  )}
                </For>
              </div>
              <div class="history-row-meta">
                {formatDate(e().atUtc)}
                {' · '}
                <span class="history-row-status">{e().statusLabel ?? 'Posted'}</span>
                <Show
                  when={
                    e().decidedByDisplayName && e().decidedByDisplayName !== props.childDisplayName
                  }
                >
                  <span class="history-row-meta-decider">
                    {' · '}
                    {(e().statusCode ?? 'posted') === 'rejected' ? 'Rejected by ' : 'Approved by '}
                    {e().decidedByDisplayName}
                  </span>
                </Show>
              </div>
              <Show when={e().decisionReason}>
                {(reason) => <div class="history-row-outcome">Reason: {reason()}</div>}
              </Show>
              <Show when={reportContext()}>
                {(context) => (
                  <div class="history-row-actions">
                    <ReportContentButton context={context()} />
                  </div>
                )}
              </Show>
            </div>
          </li>
        )}
      </Match>

      <Match when={props.entry.kind === 'withdrawal' && props.entry}>
        {(e) => (
          <li class="history-row history-row--withdrawal">
            <div class="history-row-leading">
              <span class="history-sign history-sign--minus">−</span>
              <span class="history-amount">{formatAmount(e().amountMinorUnits)}</span>
            </div>
            <div class="history-row-body">
              <div class="history-row-title">
                {(e().statusCode ?? 'posted') === 'posted' ? 'Spent from' : 'Withdrawal from'}{' '}
                {e().bucketName} · {e().reasonLabel}
              </div>
              <Show when={e().reasonNote}>
                {(note) => <div class="history-row-note">"{note()}"</div>}
              </Show>
              <div class="history-row-meta">
                {formatDate(e().atUtc)}
                {' · '}
                <span class="history-row-status">{e().statusLabel ?? 'Posted'}</span>
                <Show
                  when={
                    e().decidedByDisplayName && e().decidedByDisplayName !== props.childDisplayName
                  }
                >
                  <span class="history-row-meta-decider">
                    {' · '}
                    {(e().statusCode ?? 'posted') === 'rejected' ? 'Rejected by ' : 'Approved by '}
                    {e().decidedByDisplayName}
                  </span>
                </Show>
                <Show when={e().reconciled}>
                  <span
                    class="history-row-reconciled-chip"
                    data-testid={`history-withdrawal-${e().withdrawalId}-reconciled-chip`}
                    title="A guardian has confirmed the matching real-world cash was handed over."
                  >
                    {' · ✓ Reconciled'}
                  </span>
                </Show>
              </div>
              <Show when={e().decisionReason}>
                {(reason) => <div class="history-row-outcome">Reason: {reason()}</div>}
              </Show>
              <Show when={reportContext()}>
                {(context) => (
                  <div class="history-row-actions">
                    <ReportContentButton context={context()} />
                  </div>
                )}
              </Show>
              {/* US-WITHDRAWAL-05 -- guardian-only toggle. Children
                  never see this button. The backend also rejects
                  the call from a child session, so this is purely
                  a UI affordance. */}
              <Show when={props.isGuardian}>
                <div class="history-row-actions">
                  <button
                    type="button"
                    class="ghost"
                    disabled={props.busyWithdrawalId === e().withdrawalId}
                    onClick={() => props.onToggleReconciled(e().withdrawalId, e().reconciled)}
                    data-testid={`history-withdrawal-${e().withdrawalId}-reconcile-toggle`}
                  >
                    {e().reconciled ? 'Unmark reconciled' : 'Mark reconciled'}
                  </button>
                </div>
              </Show>
            </div>
          </li>
        )}
      </Match>

      <Match when={props.entry.kind === 'transfer' && props.entry}>
        {(e) => (
          <li class="history-row history-row--transfer">
            <div class="history-row-leading">
              <span class="history-sign">↔</span>
              <span class="history-amount">{formatAmount(e().amountMinorUnits)}</span>
            </div>
            <div class="history-row-body">
              <div class="history-row-title">
                Moved from {e().sourceBucketName}
                <Show when={e().targetBucketName}>{(target) => <> to {target()}</>}</Show>
              </div>
              <Show when={e().note}>
                {(note) => <div class="history-row-note">"{note()}"</div>}
              </Show>
              <div class="history-row-meta">
                {formatDate(e().atUtc)} ·{' '}
                <span class="history-row-status">{e().statusLabel ?? 'Posted'}</span>
                <Show
                  when={
                    e().decidedByDisplayName && e().decidedByDisplayName !== props.childDisplayName
                  }
                >
                  <span class="history-row-meta-decider">
                    {' · '}
                    {(e().statusCode ?? 'posted') === 'rejected' ? 'Rejected by ' : 'Moved by '}
                    {e().decidedByDisplayName}
                  </span>
                </Show>
              </div>
              <Show when={e().decisionReason}>
                {(reason) => <div class="history-row-outcome">Reason: {reason()}</div>}
              </Show>
              <Show when={reportContext()}>
                {(context) => (
                  <div class="history-row-actions">
                    <ReportContentButton context={context()} />
                  </div>
                )}
              </Show>
            </div>
          </li>
        )}
      </Match>
    </Switch>
  );
};

// US-WITHDRAWAL-05 -- error mapper for the toggle. Mirrors the
// shape of error helpers used in other screens.
function humanReadable(error: AppError): string {
  switch (error.kind) {
    case 'unauthorized':
      return 'Only a guardian can reconcile withdrawals. Please sign in again.';
    case 'not_found':
      return 'That withdrawal could not be found.';
    case 'conflict':
      return error.message;
    case 'validation':
      return error.message;
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
